/**
 * Mini-Project Status API client. DEV-1079, Chunk A of the Progress redesign.
 *
 * The Status section is the canonical record-of-truth for everything that
 * moves on the Mini-Project Progress tab — Schedule planned/actual, Mission
 * per-goal assessments, photos, narrative.
 *
 * Edit semantics: a status's reporting_date is immutable (it IS the slot
 * identifier), but the contents (narrative, planned/actual, photos, goal
 * assessments) are editable by the Lead via PUT. Typos and missed photos
 * need a fix path — strict append-only was too rigid for a single-author
 * surface.
 *
 * Photo upload itself goes through the existing fileStorage uploadFile
 * pipeline. This client just references the resulting storage_key when
 * creating a status with attached photos. (Earlier draft used media_link_guid
 * but migration 174 simplified to direct storage_key — see migration header.)
 *
 * See SPEC_MINI_PROJECT_PROGRESS_REDESIGN.md and DEV-1077 for context.
 */

import { apiFetch } from "./client";
import { dispatchMiniProjectMutated } from "./miniProjects";

/* ────────────────────────────────────────────────────────────────
 * Types
 * ──────────────────────────────────────────────────────────────── */

/** Per-goal assessment state at a status update. Five values. */
export type MiniProjectAssessment =
  | "ahead"
  | "on_track"
  | "behind"
  | "blocked"
  | "done";

/** Lead's qualitative budget judgement at a status. Four-state ladder
 *  (migration 190, Skip 2026-05-28).
 *
 *   - under_budget   → green (spending less than planned, surplus)
 *   - on_track       → green (within tolerance, normal progress)
 *   - trending_over  → yellow (exceeding plan, recoverable)
 *   - over_budget    → red (significantly over plan, intervention needed)
 *
 *  NULL on the wire (modeled as `null` or `undefined` here) means
 *  "not yet assessed" — Budget KPI dot stays grey. */
export type MiniProjectBudgetAssessment =
  | "under_budget"
  | "on_track"
  | "trending_over"
  | "over_budget";

export interface MiniProjectStatusPhoto {
  statusPhotoGuid: string;
  storageKey: string;
  caption?: string | null;
  position: number;
}

export interface MiniProjectStatusGoalAssessment {
  goalGuid: string;
  assessment: MiniProjectAssessment;
}

/** Full status payload — returned from GET single / latest and POST endpoints. */
export interface MiniProjectStatus {
  statusGuid: string;
  miniProjectGuid: string;
  /** ISO date (YYYY-MM-DD), Lead-chosen at posting time. */
  reportingDate: string;
  /** TipTap serialized HTML. Renderer sanitizes on display. */
  narrativeHtml: string;
  plannedPct: number;
  actualPct: number;
  postedByUserGuid: string;
  /** ISO timestamp, server-set. */
  postedAt: string;
  photos: MiniProjectStatusPhoto[];
  goalAssessments: MiniProjectStatusGoalAssessment[];
  /** Lead's budget judgement (migration 190). Null when not yet assessed. */
  budgetAssessment?: MiniProjectBudgetAssessment | null;
  /** DEV-1296: is this status currently published to the project Journal? */
  postedToJournal?: boolean;
  journalHeadline?: string | null;
  postedToFeed?: boolean;
}

/** Lightweight summary returned from the list endpoint — no narrative,
 *  no photos, no assessments. Enough to populate the calendar picker
 *  AND the Schedule KPI modal's planned-vs-actual time series
 *  (DEV-1080, Chunk B). */
export interface MiniProjectStatusSummary {
  statusGuid: string;
  reportingDate: string;
  postedByUserGuid: string;
  postedAt: string;
  plannedPct: number;
  actualPct: number;
  /** Lead's budget judgement (migration 190). Included on the summary
   *  so portfolio Budget KPI rollups don't fan out N full status fetches. */
  budgetAssessment?: MiniProjectBudgetAssessment | null;
}

/* ────────────────────────────────────────────────────────────────
 * Request shapes
 * ──────────────────────────────────────────────────────────────── */

export interface CreateMiniProjectStatusPhotoItem {
  storageKey: string;
  caption?: string | null;
  position?: number;
}

export interface CreateMiniProjectStatusGoalAssessmentItem {
  goalGuid: string;
  assessment: MiniProjectAssessment;
}

export interface CreateMiniProjectStatusRequest {
  /** ISO date (YYYY-MM-DD), not in the future, not a duplicate. */
  reportingDate: string;
  /** TipTap-serialized HTML; must contain non-whitespace text. */
  narrativeHtml: string;
  /** 0-100. */
  plannedPct: number;
  /** 0-100. */
  actualPct: number;
  /** Optional. Soft cap of 6 enforced server-side. */
  photos?: CreateMiniProjectStatusPhotoItem[];
  /** Required: every active goal on the project must be assessed. */
  goalAssessments: CreateMiniProjectStatusGoalAssessmentItem[];
  /** Optional budget assessment (migration 190). Null/omitted = not assessed. */
  budgetAssessment?: MiniProjectBudgetAssessment | null;
  /** DEV-1296: also publish this status to the project Journal (Lead-only,
   *  write/edit-time). False on a later write/edit removes the journal entry. */
  postToJournal?: boolean;
  /** Optional Journal headline; blank → the card leads with the status date. */
  journalHeadline?: string | null;
  /** Phase B: also surface in the community/org feed (syndication deferred). */
  postToFeed?: boolean;
}

/** Edit an existing status. Same shape as Create except reportingDate is
 *  omitted — the date is immutable (slot identifier). */
export interface UpdateMiniProjectStatusRequest {
  narrativeHtml: string;
  plannedPct: number;
  actualPct: number;
  /** Full replacement photo list (soft cap 6). Existing photos absent
   *  from this list are deleted server-side. */
  photos?: CreateMiniProjectStatusPhotoItem[];
  /** Every active goal on the project must be assessed. */
  goalAssessments: CreateMiniProjectStatusGoalAssessmentItem[];
  /** Budget assessment (migration 190). Sending null explicitly clears
   *  any previously-recorded value — status update is wholesale-replace
   *  by convention, unlike the per-field-conditional MiniProjectBomItem
   *  update pattern. */
  budgetAssessment?: MiniProjectBudgetAssessment | null;
  /** DEV-1296: publish/refresh (or, when false, remove) the Journal post. */
  postToJournal?: boolean;
  journalHeadline?: string | null;
  postToFeed?: boolean;
}

export interface QuickUpdateMiniProjectStatusRequest {
  /** Required one-line narrative explaining the change. */
  narrativeHtml: string;
  /** The single goal whose assessment is changing. All other goals'
   *  assessments + planned/actual carry forward from the previous status. */
  goalAssessment: CreateMiniProjectStatusGoalAssessmentItem;
}

/* ────────────────────────────────────────────────────────────────
 * Endpoints
 * ──────────────────────────────────────────────────────────────── */

export interface ListMiniProjectStatusesOptions {
  limit?: number;
  offset?: number;
}

function buildListQuery(opts: ListMiniProjectStatusesOptions = {}): string {
  const parts: string[] = [];
  if (opts.limit !== undefined) parts.push(`limit=${encodeURIComponent(String(opts.limit))}`);
  if (opts.offset !== undefined) parts.push(`offset=${encodeURIComponent(String(opts.offset))}`);
  return parts.length > 0 ? `?${parts.join("&")}` : "";
}

/** List statuses newest first. Returns summaries (no narrative body). */
export const fetchMiniProjectStatuses = (
  miniProjectGuid: string,
  opts: ListMiniProjectStatusesOptions = {},
) =>
  apiFetch<MiniProjectStatusSummary[]>(
    `/api/mini-projects/${miniProjectGuid}/statuses${buildListQuery(opts)}`,
  );

/** Fetch the most recent status with full payload. Returns null on 404
 *  (project has no statuses yet) so callers can render empty state cleanly. */
export async function fetchLatestMiniProjectStatus(
  miniProjectGuid: string,
): Promise<MiniProjectStatus | null> {
  try {
    return await apiFetch<MiniProjectStatus>(
      `/api/mini-projects/${miniProjectGuid}/statuses/latest`,
    );
  } catch (err: any) {
    if (err?.status === 404) return null;
    throw err;
  }
}

/** Fetch a single status by GUID with full payload. */
export const fetchMiniProjectStatus = (miniProjectGuid: string, statusGuid: string) =>
  apiFetch<MiniProjectStatus>(
    `/api/mini-projects/${miniProjectGuid}/statuses/${statusGuid}`,
  );

/** Create a new status. Lead-only. Server enforces uniqueness on
 *  (mini_project_guid, reporting_date) — the second POST for the same
 *  date returns a 400. To amend an existing status, use
 *  updateMiniProjectStatus instead. */
export const createMiniProjectStatus = async (
  miniProjectGuid: string,
  req: CreateMiniProjectStatusRequest,
): Promise<MiniProjectStatus> => {
  const result = await apiFetch<MiniProjectStatus>(
    `/api/mini-projects/${miniProjectGuid}/statuses`,
    { method: "POST", body: JSON.stringify(req) },
  );
  // Task #221. Wake portfolio-level consumers so the new planned/actual %
  // and per-goal assessments propagate to the KpiGridTable's Schedule
  // and Mission KPI cells without a manual reload.
  dispatchMiniProjectMutated(miniProjectGuid);
  return result;
};

/** Edit an existing status. Lead-only. reporting_date is immutable on
 *  the server side; this request body intentionally omits it. Photos and
 *  goal assessments are wholesale-replaced from the request body. */
export const updateMiniProjectStatus = async (
  miniProjectGuid: string,
  statusGuid: string,
  req: UpdateMiniProjectStatusRequest,
): Promise<MiniProjectStatus> => {
  const result = await apiFetch<MiniProjectStatus>(
    `/api/mini-projects/${miniProjectGuid}/statuses/${statusGuid}`,
    { method: "PUT", body: JSON.stringify(req) },
  );
  // Task #221. Edits to planned/actual % or goal assessments must wake
  // portfolio consumers — same rationale as createMiniProjectStatus.
  dispatchMiniProjectMutated(miniProjectGuid);
  return result;
};

/** Fast-path: post a minimal status that changes only one goal's
 *  assessment. Server carries forward other goals' assessments and the
 *  planned/actual percentages from the previous status. Reporting date
 *  defaults to today server-side.
 *
 *  Returns 400 if today already has a status (append-only constraint). */
export const quickUpdateMiniProjectStatus = async (
  miniProjectGuid: string,
  req: QuickUpdateMiniProjectStatusRequest,
): Promise<MiniProjectStatus> => {
  const result = await apiFetch<MiniProjectStatus>(
    `/api/mini-projects/${miniProjectGuid}/statuses/quick-update`,
    { method: "POST", body: JSON.stringify(req) },
  );
  // Task #221. Quick-updates change a single goal's assessment, which
  // drives the Mission KPI roll-up on the grid. Same broadcast.
  dispatchMiniProjectMutated(miniProjectGuid);
  return result;
};
